import React from "react";
import { Box, Typography, CardContent, Divider } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { NewsletterStatsDto } from "../../../shared/dtos/newsletter/NewsletterStatsDto";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

const formatAudience = (audienceStr: string): string => {
  if (!audienceStr) return "";
  return audienceStr
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface NewsletterWidgetProps {
  data: NewsletterStatsDto;
}

const NewsletterWidget: React.FC<NewsletterWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("NEWSLETTER");
  const Icon = meta.icon;

  const chartData =
    data.breakdownByAudience?.map((item) => ({
      name: formatAudience(String(item.audience)),
      value: item.count,
    })) || [];

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total This Year: {data.totalThisYear}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {chartData.length > 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              width: "100%",
              minHeight: 200,
              position: "relative",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                textAlign: "center",
                mb: 1,
              }}
            >
              Breakdown by Audience
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="45%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: "12px" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 200,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              No newsletters published yet.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default NewsletterWidget;
